import React, { useEffect, useRef, useState } from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import VolatilityWave from '../VolatilityWave';
import { useT } from '../../i18n/LanguageContext';

const ICONS = [
  // 01 · Trading — candlestick chart with trend line
  (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" className="moeg-icon text-white/70 group-hover:text-white transition-colors">
      <path d="M6 44 L20 30 L28 36 L46 14" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M46 20 L46 14 L40 14" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="14" y1="22" x2="14" y2="38" stroke="currentColor" strokeWidth="0.6" />
      <rect x="12" y="26" width="4" height="8" stroke="currentColor" strokeWidth="0.7" fill="none" />
      <line x1="26" y1="18" x2="26" y2="34" stroke="currentColor" strokeWidth="0.6" />
      <rect x="24" y="22" width="4" height="8" stroke="currentColor" strokeWidth="0.7" fill="none" />
      <line x1="38" y1="14" x2="38" y2="30" stroke="currentColor" strokeWidth="0.6" />
      <rect x="36" y="18" width="4" height="8" stroke="currentColor" strokeWidth="0.7" fill="none" />
    </svg>
  ),
  // 02 · Aktien — vertical bar chart
  (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" className="moeg-icon text-white/70 group-hover:text-white transition-colors">
      <line x1="6" y1="44" x2="46" y2="44" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <rect x="10" y="30" width="6" height="14" stroke="currentColor" strokeWidth="1" fill="none" />
      <rect x="20" y="22" width="6" height="22" stroke="currentColor" strokeWidth="1" fill="none" />
      <rect x="30" y="26" width="6" height="18" stroke="currentColor" strokeWidth="1" fill="none" />
      <rect x="40" y="14" width="6" height="30" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  ),
  // 03 · Krypto — hexagonal coin with Bitcoin-style mark
  (
    <svg width="52" height="52" viewBox="0 0 52 52" fill="none" className="moeg-icon text-white/70 group-hover:text-white transition-colors">
      <path d="M26 6 L44 16 L44 36 L26 46 L8 36 L8 16 Z" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" fill="none" />
      <path d="M22 18 h6 a4 4 0 0 1 0 8 h-6 z" stroke="currentColor" strokeWidth="0.9" fill="none" />
      <path d="M22 26 h7 a4 4 0 0 1 0 8 h-7 z" stroke="currentColor" strokeWidth="0.9" fill="none" />
      <line x1="24" y1="14" x2="24" y2="38" stroke="currentColor" strokeWidth="0.9" />
      <line x1="28" y1="14" x2="28" y2="38" stroke="currentColor" strokeWidth="0.9" />
    </svg>
  ),
];

const WAVE_VARIANTS = ['trading', 'stocks', 'crypto'];

export default function SectionMoeglichkeiten() {
  const { t, lang } = useT();
  const items = t.moeglichkeiten.items;
  const rowsRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [stigShown, setStigShown] = useState(false);

  useEffect(() => {
    const el = rowsRef.current;
    if (!el) return undefined;
    const onFragment = () => setStigShown(true);
    el.addEventListener('fragment-advance', onFragment);
    return () => el.removeEventListener('fragment-advance', onFragment);
  }, []);

  // Language-agnostic STIG contrast copy (falls back to English for non-DE
  // where translation keys are missing)
  const stigLine = t.moeglichkeiten.stigLine ||
    (lang === 'de' ? 'Diamanten' : 'Diamonds');
  const stigDrawback = t.moeglichkeiten.stigDrawback ||
    (lang === 'de' ? 'Reeller Wertspeicher' : 'Real store of value');

  useEffect(() => {
    const el = rowsRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: '-15% 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <SectionShell id="moeglichkeiten" centerContent className="">
      <div className="w-full">
        <div className="mb-6">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <div className="overline mb-2">{t.moeglichkeiten.overline}</div>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-white">
                  {t.moeglichkeiten.title}
                </h2>
              </div>
              <p className="font-ui text-2xl lg:text-3xl text-white/60 max-w-[46ch] leading-relaxed">
                {t.moeglichkeiten.subtitle}
              </p>
            </div>
          </Reveal>
        </div>
        <div
          ref={rowsRef}
          data-inview={inView ? 'true' : 'false'}
          data-fragments-remaining={stigShown ? 0 : 1}
          className="col-span-12 md:col-span-7 flex flex-col gap-px hairline border-t border-white/10 moeg-rows w-full"
        >
          {items.map((it, i) => (
            <Reveal key={i} delay={i * 120}>
              <div
                data-testid={`alt-${i}`}
                className="moeg-row md:grid md:grid-cols-[minmax(0,1fr)_380px_460px] md:items-center gap-6 py-10 px-2 border-b border-white/8 group transition-colors hover:bg-white/[0.015]"
                style={{ '--i': i }}
              >
                <div className="flex items-center gap-8 min-w-0">
                  <span className="rank-number text-4xl md:text-5xl shrink-0">0{i + 1}</span>
                  <div className="shrink-0" data-testid={`alt-icon-${i}`}>
                    {ICONS[i]}
                  </div>
                  <div className="min-w-0">
                    <div className="overline mb-2 text-white/50 text-base">{it.tag}</div>
                    <div className="font-display text-3xl md:text-5xl font-semibold text-[#f2d979]">{it.name}</div>
                  </div>
                </div>

                {/* animated volatility waveform */}
                <div
                  className="moeg-wave-wrap hidden md:flex justify-center items-center shrink-0 text-white/50"
                  data-testid={`alt-wave-${i}`}
                  aria-hidden="true"
                >
                  <VolatilityWave variant={WAVE_VARIANTS[i]} width={380} height={76} />
                </div>

                <div className="font-ui text-2xl md:text-4xl text-white/70 md:text-right md:pl-4 leading-snug">
                  {it.drawback}
                </div>
              </div>
            </Reveal>
          ))}

          {/* STIG ROCK contrast row */}
          <Reveal delay={items.length * 120 + 200}>
            <div
              data-testid="alt-stig"
              className={`moeg-row moeg-row--stig moeg-stig-frag ${stigShown ? 'moeg-stig-frag--visible' : ''} relative md:grid md:grid-cols-[minmax(0,1fr)_380px_460px] md:items-center gap-6 py-10 px-2 mt-2`}
              style={{ '--i': items.length }}
            >
              <div className="flex items-center gap-8 min-w-0">
                <span className="rank-number text-4xl md:text-5xl shrink-0 text-white/70">→</span>
                <div className="shrink-0" data-testid="alt-icon-stig" aria-hidden="true">
                  <svg width="56" height="56" viewBox="0 0 96 96" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round" className="moeg-icon-stig">
                    <path d="M48 1.5v7M31 5l4 5.5M65 5l-4 5.5" strokeWidth="1.6" opacity="0.75" />
                    <g transform="translate(0, 10)">
                      <path d="M75 15 21 15 3 38 48 83 48 83 93 38ZM30.69 40 43.8 70.32 13.48 40ZM61 40 48 70 35 40ZM34.71 36 39.32 21 56.68 21 61.29 36ZM65.31 40 82.51 40 52.22 70.29ZM83.82 36 65.48 36 60.86 21 72.08 21ZM23.92 21 35.14 21 30.52 36 12.18 36Z" />
                    </g>
                  </svg>
                </div>
                <div className="min-w-0">
                  <div className="overline mb-2 text-white/60 text-base tracking-[0.34em]">STIG ROCK</div>
                  <div className="font-display text-4xl md:text-5xl font-semibold text-white">
                    {stigLine}
                  </div>
                </div>
              </div>

              <div
                className="moeg-wave-wrap hidden md:flex justify-center items-center shrink-0"
                aria-hidden="true"
              >
                <svg viewBox="0 0 280 56" width={380} height={76} className="moeg-stig-line" aria-hidden="true">
                  {/* Realistic physical-asset trajectory — small dips
                      followed by a steady, resilient long-term climb.
                      Not a straight line: value stores breathe, but the
                      trend is stubbornly upward. */}
                  <path
                    d="M0,46 C18,43 30,45 46,42 S78,32 96,34 S128,40 148,34 S184,20 208,22 S248,14 268,12 L280,10"
                    fill="none"
                    stroke="rgba(242,217,121,0.85)"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="moeg-stig-line__path"
                  />
                  <circle cx="280" cy="10" r="3.5" fill="rgba(242,217,121,0.95)" className="moeg-stig-line__dot" />
                </svg>
              </div>

              <div className="font-ui text-2xl md:text-4xl text-white/85 md:text-right md:pl-4 leading-snug" data-testid="alt-stig-desc">
                {stigDrawback}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </SectionShell>
  );
}
